import React from 'react';
import { Helmet } from 'react-helmet';

const BusinessSchema = ({ pageType = 'website' }) => {
  const SITE_URL = 'https://sacredrelm.com';
  
  // Organization Schema - Use on all pages
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    "name": "Sacred Relm",
    "alternateName": "Sacredrelm",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo.png`,
    "description": "Sacred Relm is a premium online store specializing in authentic Karungali products, spiritual items, and sacred accessories. We offer genuine ebony wood bracelets, malas, and spiritual artifacts for wellness and positive energy.",
    "email": "customercareproductcenter@gmail.com",
    "telephone": "+91-9059821555",
    "address": {
      "@type": "Hyderabad, Telangana",
      "streetAddress": "Hyderabad, Telangana",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500016",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+91-9059821555",
        "contactType": "customer service",
        "availableLanguage": ["English", "Hindi", "Telugu"],
        "areaServed": "IN"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+91-9059821555",
        "contactType": "sales",
        "availableLanguage": ["English", "Hindi", "Telugu"],
        "areaServed": "IN"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/sacredrelm",
      "https://www.youtube.com/@sacredrelm",
      "https://wa.me/919059821555"
    ],
    "foundingDate": "2024",
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "UPI", "Net Banking", "Razorpay"],
    "priceRange": "₹₹"
  };

  // LocalBusiness/OnlineStore Schema - Use on Homepage
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    "@id": `${SITE_URL}/#localbusiness`,
    "name": "Sacred Relm",
    "image": `${SITE_URL}/logo.png`,
    "url": SITE_URL,
    "telephone": "+91-9059821555",
    "email": "customercareproductcenter@gmail.com",
    "description": "Premium online spiritual store offering authentic Karungali products, ebony wood bracelets, malas, and sacred accessories for spiritual wellness and positive energy.",
    "address": {
      "@type": "Hyderabad, Telangana",
      "streetAddress": "Hyderabad, Telangana",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500016",
      "addressCountry": "IN"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "10:00",
        "closes": "20:00"
      }
    ],
    "priceRange": "₹₹",
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "UPI", "Net Banking", "Razorpay"],
    "currenciesAccepted": "INR",
    "sameAs": [
      "https://www.instagram.com/sacredrelm",
      "https://www.youtube.com/@sacredrelm"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  // WebSite Schema - Use on Homepage
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "url": SITE_URL,
    "name": "Sacred Relm",
    "description": "Premium spiritual products and authentic Karungali items",
    "publisher": {
      "@id": `${SITE_URL}/#organization`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/shop?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // BreadcrumbList Schema - Use on inner pages
  const getBreadcrumbSchema = () => {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(part => part);
    
    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        }
      ]
    };

    pathParts.forEach((part, index) => {
      const name = part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
      breadcrumbList.itemListElement.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": name,
        "item": `${SITE_URL}/${pathParts.slice(0, index + 1).join('/')}`
      });
    });

    return breadcrumbList;
  };

  // Determine which schemas to include based on page type
  const getSchemas = () => {
    switch (pageType) {
      case 'home':
        return [organizationSchema, localBusinessSchema, websiteSchema];
      case 'product':
        // Product schema is handled in ProductPage component
        return [organizationSchema, getBreadcrumbSchema()];
      case 'about':
      case 'contact':
      case 'store':
      case 'category':
        return [organizationSchema, getBreadcrumbSchema()];
      default:
        return [organizationSchema];
    }
  };

  const schemas = getSchemas();

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default BusinessSchema;
